import React, { useEffect, useMemo, useState } from "react";
import { Col, Container, Form, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { GeneralApi } from "../../Api/General";
import { QuizAPI } from "../../Api/Quiz";
import { Button, Input, UploadButton } from "../../Components/CustomComponent";
import { Toast } from "../../Hooks/useToast";
import styles from "./index.module.scss";
import moment from "moment";

export default function CreateQuiz() {
  const queryClient = useQueryClient();
  const quizAPI = QuizAPI();
  const generalApi = GeneralApi();

  const navigate = useNavigate();
  const location = useLocation();

  const quiz = useMemo(() => location.state?.quiz, [location.state?.quiz]);
  const isMultileFileUpload = useMemo(() => false, []);
  const fileAcceptParams = useMemo(() => "image/png, image/jpeg", []);

  const [file, setFile] = useState(null);
  const [imageFiles, setImageFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    start_at: "",
    end_at: "",
    contestant_count: "",
    audience_count: "",
    audience_poll_duration: "",
    expert_help_duration: "",
    google_help_duration: "",
    cover_image_url: "",
  });

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      title: quiz?.title,
      description: quiz?.description,
      contestant_count: quiz?.contestant_count,
      audience_count: quiz?.audience_count,
      audience_poll_duration: quiz?.audience_poll_duration,
      expert_help_duration: quiz?.expert_help_duration,
      google_help_duration: quiz?.google_help_duration,
      cover_image_url: quiz?.cover_image_url,
    },
  });
  const cover_image_url = watch("cover_image_url");
  const start_at = watch("start_at");
  const end_at = watch("end_at");

  const quizMutation = useMutation(quizAPI.create, {
    onSuccess: (data) => {
      Toast("Quiz created successfully!", "success", false);
      queryClient.invalidateQueries(
        ["quizz"],
        {
          refetchInactive: true,
        },
        {}
      );
      navigate(-1);
    },
    onError: (err) => {
      Toast(err.message || "Something went wrong!", "error", false);
      setLoading(false);
    },
  });


  useEffect(() => {
    if (!start_at) return

    if (moment(start_at).isAfter(moment(end_at))) {
      setValue('end_at', moment(moment(start_at).add(15, 'minutes')).toDate())
    }
  }, [start_at])

  const uploadMutation = useMutation(generalApi.upload, {
    onSuccess: (data) => { },
    onError: (err) => {
      Toast(err.message, "error", false);
      setLoading(false);
    },
  });

  const handleAddfilesAndSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFile(file);
      const doc = URL.createObjectURL(file);
      setImageFiles([...imageFiles, doc]);
    }
  };

  const onSubmit = async (data) => {
    if (loading) return;
    setLoading(true);

    if (file) {
      const formDataForUpload = new FormData();
      formDataForUpload.append("file", file);
      formDataForUpload.append("path", "quizzes");

      const uploadResponse = await uploadMutation.mutateAsync(
        formDataForUpload
      );
      data.cover_image_url = uploadResponse?.data[0];
    }
    let payload;
    if (quiz) {
      payload = { ...data, quiz_id: quiz?.id };
    } else {
      payload = data;
    }
    await quizMutation.mutate(payload);
  };


  return (
    <Container className={styles.CreateQuiz}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <Col sm={12}>
            <UploadButton
              label="Upload Cover"
              classes="mb-2"
              labelClasses="p-5"
              isMultileFileUpload={isMultileFileUpload}
              fileAcceptParams={fileAcceptParams}
              file={file || cover_image_url}
              handleFileChange={handleAddfilesAndSelect}
            />
          </Col>

          <Col sm={12}>
            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  label="Quiz Title"
                  placeholder="Add Here"
                  errors={errors}
                />
              )}
            />
          </Col>

          <Col sm={6}>
            <Controller
              name="contestant_count"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  label="Contestant"
                  placeholder="1 - 99"
                  labelClasses="fs-7"
                  type="number"
                />
              )}
            />
          </Col>

          <Col sm={6}>
            <Controller
              name="audience_count"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  label="Audience Count"
                  placeholder="1 - 99"
                  labelClasses="fs-7"
                  type="number"
                />
              )}
            />
          </Col>

          <Col md={6}>
            <Controller
              name="start_at"
              control={control}
              render={({ field }) => (
                <Input {...field}
                  label="Start Date"
                  type="date"
                  minTime={
                    (!start_at || moment(start_at).isSame(new Date(), 'day')) ?
                      moment().toDate()
                      : undefined
                  }
                />
              )}
            />
          </Col>

          <Col md={6}>
            <Controller
              name="end_at"
              control={control}
              render={({ field }) => (
                <Input {...field}
                  label="End Date"
                  type="date"
                  minDate={start_at ? moment(start_at) : moment()}
                  minTime={
                    (start_at && end_at && moment(start_at).isSame(moment(end_at), 'day'))
                      ? moment(moment(start_at).add(15, 'minutes')).toDate()
                      : undefined
                  }
                />
              )}
            />
          </Col>

          <Col sm={6}>
            <Controller
              name="audience_poll_duration"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  label="Audience Poll Duration"
                  placeholder="60"
                  labelClasses="fs-7"
                  type="number"
                />
              )}
            />
          </Col>

          <Col sm={6}>
            <Controller
              name="expert_help_duration"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  label="Expert Help Duration"
                  placeholder="60"
                  labelClasses="fs-7"
                  type="number"
                />
              )}
            />
          </Col>

          <Col sm={6}>
            <Controller
              name="google_help_duration"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  label="Google Help Duration"
                  placeholder="60"
                  labelClasses="fs-7"
                  type="number"
                />
              )}
            />
          </Col>

          <Col sm={12}>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  as="textarea"
                  label="Description"
                  labelClasses="fs-7"
                  placeholder="Add Here"
                  rows={8}
                />
              )}
            />
          </Col>
        </Row>
        <Button
          type="submit"
          text={"Submit"}
          classes="mt-2"
          isSubmitted={loading}
        />
      </Form>
    </Container>
  );
}
